/**
 * Contacts tab (SPEC screen 4). Contact PII sits behind its own
 * `partners.party_contact.*` grants.
 *
 * The create schema is an anyOf over {contact_name, email, phone}: at least
 * one must be present, and the resulting active contact must keep one, so the
 * form refuses an all-empty contact instead of letting the server 400.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  archivePartyContact,
  createPartyContact,
  getPartyContact,
  updatePartyContact,
  type PartyContact,
  type PartyContactCreate,
} from "../api";
import { partnersKeys, usePartyContacts } from "../queries";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ContactDraft {
  contactName: string;
  email: string;
  phone: string;
  role: string;
  isPrimary: boolean;
}

const EMPTY_DRAFT: ContactDraft = {
  contactName: "",
  email: "",
  phone: "",
  role: "",
  isPrimary: false,
};

export interface ContactsPanelProps {
  partyId: string;
  partyEditable: boolean;
  canRead?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canArchive?: boolean;
}

export function ContactsPanel({
  partyId,
  partyEditable,
  canRead = true,
  canCreate = true,
  canUpdate = true,
  canArchive = true,
}: ContactsPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [includeArchived, setIncludeArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: string; etag: string | null } | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<PartyContact | null>(null);

  const contactsQuery = usePartyContacts(partyId, includeArchived, canRead);
  const contacts = contactsQuery.data ?? [];

  useEffect(() => {
    if (!dialogOpen) {
      setEditing(null);
      setFormError(null);
    }
  }, [dialogOpen]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: partnersKeys.parties.all });
  };

  const loadForEdit = useMutation({
    mutationFn: (contactId: string) => getPartyContact(partyId, contactId),
    onSuccess: ({ data, etag }) => {
      setEditing({ id: data.id, etag });
      setDraft({
        contactName: data.contact_name ?? "",
        email: data.email ?? "",
        phone: data.phone ?? "",
        role: data.role ?? "",
        isPrimary: data.is_primary,
      });
      setFormError(null);
      setDialogOpen(true);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const createMutation = useIdempotentMutation<PartyContact, PartyContactCreate>({
    mutationFn: (input, key) => createPartyContact(partyId, input, key),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const updateMutation = useMutation({
    mutationFn: (input: {
      contactId: string;
      patch: Parameters<typeof updatePartyContact>[2];
      ifMatch: string;
    }) => updatePartyContact(partyId, input.contactId, input.patch, input.ifMatch),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const archiveMutation = useMutation({
    mutationFn: (contactId: string) => archivePartyContact(partyId, contactId),
    onSuccess: () => {
      setArchiveTarget(null);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const set = <K extends keyof ContactDraft>(key: K, value: ContactDraft[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setFormError(null);
  };

  const submit = (): void => {
    const name = draft.contactName.trim();
    const email = draft.email.trim();
    const phone = draft.phone.trim();
    const role = draft.role.trim();
    if (name === "" && email === "" && phone === "") {
      setFormError(t("partners.contacts.oneOfRequired"));
      return;
    }
    if (email !== "" && !EMAIL_RE.test(email)) {
      setFormError(t("partners.contacts.emailInvalid"));
      return;
    }
    setFormError(null);
    if (editing === null) {
      createMutation.mutate({
        ...(name === "" ? {} : { contact_name: name }),
        ...(email === "" ? {} : { email }),
        ...(phone === "" ? {} : { phone }),
        ...(role === "" ? {} : { role }),
        is_primary: draft.isPrimary,
      });
      return;
    }
    if (editing.etag === null) {
      setFormError(t("partners.contacts.oneOfRequired"));
      return;
    }
    updateMutation.mutate({
      contactId: editing.id,
      ifMatch: editing.etag,
      patch: {
        contact_name: name === "" ? null : name,
        email: email === "" ? null : email,
        phone: phone === "" ? null : phone,
        role: role === "" ? null : role,
        is_primary: draft.isPrimary,
      },
    });
  };

  if (!canRead) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t("errors.forbidden", { permission: "partners.party_contact.read" })}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle>
          <h2>{t("partners.contacts.title")}</h2>
        </CardTitle>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="contacts-include-archived"
              checked={includeArchived}
              onCheckedChange={(checked) => setIncludeArchived(checked === true)}
            />
            <Label htmlFor="contacts-include-archived" className="font-normal">
              {t("partners.common.showArchived")}
            </Label>
          </div>
          {canCreate && partyEditable ? (
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setEditing(null);
                setDraft(EMPTY_DRAFT);
                setFormError(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              {t("partners.contacts.add")}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {contactsQuery.isError ? (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
            <span>{t("partners.contacts.loadError")}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void contactsQuery.refetch()}
            >
              {t("actions.retry")}
            </Button>
          </div>
        ) : contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("partners.contacts.empty")}</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {contacts.map((contact) => (
              <li
                key={contact.id}
                className="flex flex-wrap items-center gap-3 py-2"
                data-testid="partners-contact-row"
              >
                <span dir="auto" className="min-w-40 flex-1 font-medium">
                  {contact.contact_name ?? "—"}
                </span>
                <span dir="ltr" className="min-w-48 flex-1 text-sm text-muted-foreground">
                  {contact.email ?? "—"}
                </span>
                <span dir="ltr" className="min-w-32 text-sm text-muted-foreground">
                  {contact.phone ?? "—"}
                </span>
                <span dir="auto" className="min-w-24 text-sm text-muted-foreground">
                  {contact.role ?? "—"}
                </span>
                {contact.is_primary ? (
                  <Badge variant="default">{t("partners.common.primaryBadge")}</Badge>
                ) : null}
                {contact.archived_at !== undefined ? (
                  <Badge variant="secondary">{t("partners.common.archivedBadge")}</Badge>
                ) : null}
                {partyEditable && contact.archived_at === undefined ? (
                  <span className="flex items-center gap-1">
                    {canUpdate ? (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={t("partners.contacts.edit")}
                        disabled={loadForEdit.isPending}
                        onClick={() => loadForEdit.mutate(contact.id)}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                    {canArchive ? (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={t("partners.common.archive")}
                        onClick={() => setArchiveTarget(contact)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>
              {editing === null
                ? t("partners.contacts.createTitle")
                : t("partners.contacts.editTitle")}
            </DialogTitle>
            <DialogDescription>{t("partners.contacts.oneOfRequired")}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="contact-name">{t("partners.contacts.name")}</Label>
              <Input
                id="contact-name"
                dir="auto"
                value={draft.contactName}
                onChange={(event) => set("contactName", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contact-email">{t("partners.contacts.email")}</Label>
              <Input
                id="contact-email"
                dir="ltr"
                type="email"
                autoComplete="off"
                value={draft.email}
                onChange={(event) => set("email", event.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t("partners.contacts.emailHint")}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contact-phone">{t("partners.contacts.phone")}</Label>
              <Input
                id="contact-phone"
                dir="ltr"
                value={draft.phone}
                onChange={(event) => set("phone", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="contact-role">{t("partners.contacts.role")}</Label>
              <Input
                id="contact-role"
                dir="auto"
                list="partners-contact-roles"
                placeholder={t("partners.contacts.rolePlaceholder")}
                value={draft.role}
                onChange={(event) => set("role", event.target.value)}
              />
              <datalist id="partners-contact-roles">
                <option value="buyer" />
                <option value="accounts" />
                <option value="logistics" />
              </datalist>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox
                id="contact-primary"
                checked={draft.isPrimary}
                onCheckedChange={(checked) => set("isPrimary", checked === true)}
              />
              <Label htmlFor="contact-primary" className="font-normal">
                {t("partners.contacts.isPrimary")}
              </Label>
            </div>
          </div>

          {formError !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={createMutation.isPending || updateMutation.isPending}
              onClick={submit}
            >
              {t("actions.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveTarget !== null} onOpenChange={() => setArchiveTarget(null)}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("partners.contacts.archiveTitle")}</DialogTitle>
            <DialogDescription>{t("partners.contacts.archiveBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setArchiveTarget(null)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              loading={archiveMutation.isPending}
              onClick={() => {
                if (archiveTarget !== null) archiveMutation.mutate(archiveTarget.id);
              }}
            >
              {t("partners.common.archive")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
